import { useEffect, useState } from "react";
import { DislikeFilled, DislikeOutlined, EditOutlined, LikeFilled, LikeOutlined } from "@ant-design/icons";
import { Button, Dropdown, Input, Modal, Popover, Space, Typography, type MenuProps } from "antd";
import { useTranslation } from "react-i18next";

import { type MessageAgentReplyFeedbackVote } from "@fitness-app/data-models/entities/Chat";
import { PermissionKey } from "@fitness-app/data-models";
import { cn } from "@fitness-app/utils/src/styles/cn";

import { usePermissionRequirement } from "~/hooks/usePermission";
import {
  type PendingChatAgentSuggestion,
  type PendingChatAgentSuggestionStatus,
} from "~/modules/AI/hooks/usePendingChatAgentSuggestions";
import { useChatAgentSuggestionAction } from "~/modules/Chat/hooks/useChatAgentSuggestionAction";

const renderFeedbackButtonClassName = ({
  vote,
  selectedVote,
}: {
  vote: MessageAgentReplyFeedbackVote;
  selectedVote: MessageAgentReplyFeedbackVote | null;
}) =>
  cn(
    "!rounded-full !shadow-none",
    vote === "up"
      ? selectedVote === "up"
        ? "!border-emerald-600 !bg-emerald-600 !text-white"
        : "!border-emerald-200 !bg-white !text-emerald-700"
      : selectedVote === "down"
        ? "!border-rose-600 !bg-rose-600 !text-white"
        : "!border-rose-200 !bg-white !text-rose-700",
  );

export const ChatAgentSuggestionReviewControls = ({ row }: { row: PendingChatAgentSuggestion }) => {
  const { t } = useTranslation(["chat", "common", "dashboard"]);
  const canReviewSuggestion = usePermissionRequirement({
    allOf: [{ permission: PermissionKey.AGENTS_USE }, { permission: PermissionKey.CHAT_WRITE }],
  });
  const acceptSuggestionMutation = useChatAgentSuggestionAction("accept");
  const dismissSuggestionMutation = useChatAgentSuggestionAction("dismiss");
  const feedbackSuggestionMutation = useChatAgentSuggestionAction("feedback");
  const regenerateSuggestionMutation = useChatAgentSuggestionAction("regenerate");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isFeedbackPopoverOpen, setIsFeedbackPopoverOpen] = useState(false);
  const [suggestionDraft, setSuggestionDraft] = useState(row.agentReplyText || "");
  const [suggestionFeedbackVote, setSuggestionFeedbackVote] = useState<MessageAgentReplyFeedbackVote | null>(
    row.agentReplyFeedbackVote || null,
  );
  const [suggestionFeedbackComment, setSuggestionFeedbackComment] = useState(row.agentReplyFeedbackComment || "");

  const isPending =
    acceptSuggestionMutation.isLoading ||
    dismissSuggestionMutation.isLoading ||
    feedbackSuggestionMutation.isLoading ||
    regenerateSuggestionMutation.isLoading;
  const trimmedSuggestionDraft = suggestionDraft.trim();
  const hasGeneratedReply = Boolean(row.agentReplyStatus && row.agentReplyText);
  const hasSavedSuggestionFeedback = Boolean(row.agentReplyFeedbackSubmittedAt);
  const savedSuggestionFeedbackVote = row.agentReplyFeedbackVote || null;
  const savedSuggestionFeedbackComment = row.agentReplyFeedbackComment?.trim() || "";
  const hasSuggestionFeedbackChanges =
    suggestionFeedbackVote !== (row.agentReplyFeedbackVote || null) ||
    suggestionFeedbackComment !== (row.agentReplyFeedbackComment || "");

  useEffect(() => {
    setSuggestionDraft(row.agentReplyText || "");
    setIsEditModalOpen(false);
    setIsFeedbackPopoverOpen(false);
    setSuggestionFeedbackVote(row.agentReplyFeedbackVote || null);
    setSuggestionFeedbackComment(row.agentReplyFeedbackComment || "");
  }, [row.id, row.agentReplyStatus, row.agentReplyText, row.agentReplyFeedbackVote, row.agentReplyFeedbackComment]);

  const handleSuggestionFeedbackSubmit = () => {
    if (!suggestionFeedbackVote) {
      return;
    }

    feedbackSuggestionMutation.mutate(
      {
        messageId: row.id,
        feedbackVote: suggestionFeedbackVote,
        feedbackComment: suggestionFeedbackComment,
      },
      {
        onSuccess: () => setIsFeedbackPopoverOpen(false),
      },
    );
  };

  const renderSuggestionFeedbackPopoverContent = () => (
    <div className="w-[280px]">
      <Typography.Text className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
        {t("chat:agentSuggestion.feedbackTitle")}
      </Typography.Text>
      <Space size={6} wrap className="mb-3">
        <Button
          size="small"
          className={renderFeedbackButtonClassName({
            vote: "up",
            selectedVote: suggestionFeedbackVote,
          })}
          disabled={isPending}
          icon={suggestionFeedbackVote === "up" ? <LikeFilled /> : <LikeOutlined />}
          onClick={() => setSuggestionFeedbackVote("up")}
        >
          {t("chat:agentSuggestion.feedbackPositive")}
        </Button>
        <Button
          size="small"
          className={renderFeedbackButtonClassName({
            vote: "down",
            selectedVote: suggestionFeedbackVote,
          })}
          disabled={isPending}
          icon={suggestionFeedbackVote === "down" ? <DislikeFilled /> : <DislikeOutlined />}
          onClick={() => setSuggestionFeedbackVote("down")}
        >
          {t("chat:agentSuggestion.feedbackNegative")}
        </Button>
      </Space>
      <Input.TextArea
        value={suggestionFeedbackComment}
        onChange={(event) => setSuggestionFeedbackComment(event.target.value)}
        autoSize={{ minRows: 2, maxRows: 4 }}
        disabled={isPending}
        placeholder={t<string>("chat:agentSuggestion.feedbackCommentPlaceholder")}
        className="mb-2"
      />
      <Space size="small" wrap>
        <Button
          size="small"
          type="primary"
          loading={feedbackSuggestionMutation.isLoading}
          disabled={isPending || !suggestionFeedbackVote || !hasSuggestionFeedbackChanges}
          onClick={handleSuggestionFeedbackSubmit}
        >
          {hasSavedSuggestionFeedback
            ? t("chat:agentSuggestion.feedbackUpdate")
            : t("chat:agentSuggestion.feedbackSave")}
        </Button>
        {hasSavedSuggestionFeedback ? (
          <Typography.Text className="text-xs text-slate-500">
            {t("chat:agentSuggestion.feedbackSaved")}
          </Typography.Text>
        ) : null}
      </Space>
    </div>
  );

  const secondaryActionItems: MenuProps["items"] = [
    {
      key: "edit",
      label: t("common:button.edit"),
      disabled: isPending,
      icon: <EditOutlined />,
      onClick: () => setIsEditModalOpen(true),
    },
    {
      key: "regenerate",
      label: t("chat:agentSuggestion.regenerate"),
      disabled: isPending,
      onClick: () => regenerateSuggestionMutation.mutate({ messageId: row.id }),
    },
  ];

  if (!hasGeneratedReply || !canReviewSuggestion) {
    return <span>-</span>;
  }

  return (
    <div className="min-w-[220px] space-y-2">
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          {hasSavedSuggestionFeedback && savedSuggestionFeedbackVote ? (
            <div
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium",
                savedSuggestionFeedbackVote === "up"
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : "border-rose-200 bg-rose-50 text-rose-700",
              )}
            >
              {savedSuggestionFeedbackVote === "up" ? <LikeFilled /> : <DislikeFilled />}
              {savedSuggestionFeedbackVote === "up"
                ? t("chat:agentSuggestion.feedbackPositive")
                : t("chat:agentSuggestion.feedbackNegative")}
            </div>
          ) : null}
          <Popover
            trigger="click"
            placement="topLeft"
            open={isFeedbackPopoverOpen}
            onOpenChange={setIsFeedbackPopoverOpen}
            content={renderSuggestionFeedbackPopoverContent()}
          >
            <Button type="link" size="small" className="!h-auto !p-0 !font-medium">
              {hasSavedSuggestionFeedback
                ? t("chat:agentSuggestion.feedbackEdit")
                : t("chat:agentSuggestion.feedbackTitle")}
            </Button>
          </Popover>
        </div>
        {hasSavedSuggestionFeedback && savedSuggestionFeedbackComment ? (
          <Typography.Paragraph
            className="!mb-0 max-w-[220px] whitespace-pre-line text-xs leading-5 text-slate-500"
            ellipsis={{ rows: 2, tooltip: savedSuggestionFeedbackComment }}
          >
            {savedSuggestionFeedbackComment}
          </Typography.Paragraph>
        ) : null}
      </div>

      {row.agentReplyStatus === ("suggested" satisfies PendingChatAgentSuggestionStatus) ? (
        <div className="space-y-2">
          <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
            <Button
              type="primary"
              size="small"
              className="!h-8 !rounded-lg !border-emerald-600 !bg-emerald-600 !font-medium !shadow-none hover:!border-emerald-500 hover:!bg-emerald-500"
              loading={acceptSuggestionMutation.isLoading}
              disabled={isPending || !trimmedSuggestionDraft}
              onClick={() => acceptSuggestionMutation.mutate({ messageId: row.id })}
            >
              {t("chat:agentSuggestion.accept")}
            </Button>
            <div className="[&_.ant-btn]:!h-8 [&_.ant-btn]:!border-slate-300 [&_.ant-btn]:!bg-white [&_.ant-btn]:!font-medium [&_.ant-btn]:!text-slate-700 [&_.ant-btn]:!shadow-none [&_.ant-dropdown-button]:!flex">
              <Dropdown.Button
                size="small"
                menu={{ items: secondaryActionItems }}
                disabled={isPending}
                loading={dismissSuggestionMutation.isLoading}
                onClick={() => dismissSuggestionMutation.mutate({ messageId: row.id })}
              >
                {t("chat:agentSuggestion.dismiss")}
              </Dropdown.Button>
            </div>
          </div>
        </div>
      ) : null}

      <Modal
        open={isEditModalOpen}
        title={t("dashboard:ai.chatAgents.review.editTitle")}
        onCancel={() => setIsEditModalOpen(false)}
        footer={
          <Space>
            <Button onClick={() => setIsEditModalOpen(false)}>{t("common:button.cancel")}</Button>
            <Button
              type="primary"
              loading={acceptSuggestionMutation.isLoading}
              disabled={isPending || !trimmedSuggestionDraft}
              onClick={() =>
                acceptSuggestionMutation.mutate(
                  {
                    messageId: row.id,
                    replyText: suggestionDraft,
                  },
                  {
                    onSuccess: () => setIsEditModalOpen(false),
                  },
                )
              }
            >
              {t("dashboard:ai.chatAgents.review.acceptEdited")}
            </Button>
          </Space>
        }
        destroyOnHidden
      >
        <Input.TextArea
          value={suggestionDraft}
          onChange={(event) => setSuggestionDraft(event.target.value)}
          autoSize={{ minRows: 5, maxRows: 10 }}
          disabled={isPending}
          placeholder={t<string>("chat:agentSuggestion.title")}
        />
      </Modal>
    </div>
  );
};
